import React, { CSSProperties, useEffect, useMemo, useState } from "react";

import { Button, FormField, FormGroup } from "semantic-ui-react";

type SubLinkAttachmentProps = {
  readOnly?: boolean;
  url: string;
  onDeleteLink: () => void;
};

type Styles = Record<"ellipsis" | "icon" | "media", CSSProperties>;

const IMAGES = {
  link: "/static/images/time-attendance/link-icon.png",
};

const styles: Styles = {
  ellipsis: {
    display: "block",
    // maxWidth: "36rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  icon: {
    height: "15px",
    width: "auto",
  },
  media: {
    height: "57px",
    objectFit: "cover" as const,
    width: "98px",
  },
};

const SubLinkAttachment = (props: SubLinkAttachmentProps) => {
  const [title, setTitle] = useState<string>("");
  const [thumbnailSource, setThumbnailSource] = useState<string>(IMAGES.link);

  const youTubeVideoId = useMemo((): string | null => {
    const url = props.url;

    if (url.includes("youtu.be/")) {
      return url.split("youtu.be/")[1]?.split("?")[0] || null;
    }

    if (url.includes("watch?v=")) {
      return url.split("watch?v=")[1]?.split("&")[0] || null;
    }

    return null;
  }, [props.url]);

  const isImageUrl = useMemo((): boolean => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg"];

    return imageExtensions.some((ext) => props.url.toLowerCase().includes(ext));
  }, [props.url]);

  const isYouTubeUrl = useMemo((): boolean => youTubeVideoId !== null, [youTubeVideoId]);

  const isMediaUrl = useMemo((): boolean => isImageUrl || isYouTubeUrl, [isImageUrl, isYouTubeUrl]);

  const fetchYouTubeTitle = async (videoUrl: string): Promise<string> => {
    try {
      const response = await fetch(
        `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(videoUrl)}`
      );
      const data = await response.json();

      return (data.title || "") as string;
    } catch {
      return "";
    }
  };

  const handleImageError = () => {
    setThumbnailSource(IMAGES.link);
  };

  useEffect(() => {
    const setupLinkData = async (): Promise<void> => {
      if (youTubeVideoId) {
        setThumbnailSource(`https://img.youtube.com/vi/${youTubeVideoId}/hqdefault.jpg`);
        const youtubeTitle = await fetchYouTubeTitle(props.url);

        setTitle(youtubeTitle);

        return;
      }

      if (isImageUrl) {
        setThumbnailSource(props.url);
      } else {
        setThumbnailSource(IMAGES.link);
      }

      setTitle("");
    };

    void setupLinkData();
  }, [isImageUrl, props.url, youTubeVideoId]);

  return (
    <FormGroup inline>
      <FormField style={{ width: "130px" }} inline>
        <div
          style={{
            alignItems: "center",
            border: "1px solid rgba(31, 30, 30, 0.29)",
            display: "flex",
            height: "57px",
            justifyContent: "center",
            width: "98px",
          }}
        >
          <img
            style={isMediaUrl ? styles.media : styles.icon}
            alt="link.icon"
            src={thumbnailSource}
            onError={handleImageError}
          />
        </div>
      </FormField>
      <FormField width={6} inline>
        <div
          style={{
            backgroundColor: "rgba(236, 236, 236, 1)",
            borderRadius: "2px",
            height: "57px",
            padding: "0.5rem 1rem",
            width: "100%",
          }}
        >
          <a
            style={{
              textDecoration: "underline",
              ...styles.ellipsis,
            }}
            href={props.url}
            rel="noreferrer"
            target="_blank"
            title={props.url}
          >
            {props.url}
          </a>
          <div
            style={{
              fontSize: "0.85rem",
              lineHeight: 1.5,
              marginTop: "0.1rem",
              ...styles.ellipsis,
            }}
          >
            {title}
          </div>
        </div>
      </FormField>
      <FormField inline>
        {!props.readOnly && (
          <Button
            style={{ fontSize: "0.55rem" }}
            color="red"
            icon="minus"
            size="mini"
            onClick={props.onDeleteLink}
          />
        )}
      </FormField>
    </FormGroup>
  );
};

SubLinkAttachment.displayName = "SubLinkAttachment";

export default React.memo(SubLinkAttachment);
